// Pool object types - matches backend pool serialization format
//
// TO ADD A NEW MODEL:
// 1. Add the type name to OBJECT_TYPES array
// 2. Add the interface to ObjectTypeMap
// That's it! PoolObject and ObjectType are derived automatically.

export type VoteResponse = 'yes' | 'no' | 'preferably_not'

export interface Permission {
  allowed: boolean
  reason?: string
}

// Base fields all pool objects must have
interface PoolObjectBase<T extends string> {
  id: string
  objectType: T
  updatedAt: string // ISO8601 with milliseconds
  permissions?: Record<string, Permission | string[]>
}

// ============================================================================
// OBJECT TYPE REGISTRY - Add new models here
// ============================================================================

export type DatePollStatus = 'open' | 'expired' | 'resolved'

export type AttendanceStatus = 'pending' | 'going' | 'declined'

export const OBJECT_TYPES = [
  'member',
  'event',
  'datePoll',
  'dateRange',
  'vote',
  'attendance',
  'guest',
  'workspace',
  'taskList',
  'taskItem',
  'expense',
  'settlement',
  'settlementTransfer',
  'choreRoster',
  'chore',
  'choreAssignment',
  'workspaceInvite',
  'expenseParticipant',
  'notification',
] as const

export interface ObjectTypeMap {
  member: PoolObjectBase<'member'> & {
    workspaceId: string
    userId: string
    email: string
    name: string | null
    phoneNumber: string | null
    birthday: string | null
    locationName: string | null
    latitude: number | null
    longitude: number | null
    role: string
    createdAt: string
  }
  event: PoolObjectBase<'event'> & {
    name: string
    description: string | null
    startDate: string | null
    endDate: string | null
    locationName: string | null
    latitude: number | null
    longitude: number | null
    // IANA zone the event happens in (e.g. "Europe/Amsterdam"). Authoritative
    // for chore times and reminders. Derived from the location, falling back to
    // the workspace default.
    timezone: string
    workspaceId: string
    userId: string
    datePollId: string | null
    attendanceIds: string[]
    createdAt: string
  }
  datePoll: PoolObjectBase<'datePoll'> & {
    eventId: string
    deadline: string
    selectedDateRangeId: string | null
    closedAt: string | null
    status: DatePollStatus
    dateRangeIds: string[]
    createdAt: string
  }
  dateRange: PoolObjectBase<'dateRange'> & {
    datePollId: string
    startDate: string
    endDate: string
  }
  vote: PoolObjectBase<'vote'> & {
    dateRangeId: string
    userId: string
    response: VoteResponse
    comment: string | null
    createdAt: string
  }
  // One row per person per event (doc/attendances.md). Exactly one of
  // userId/guestId is set.
  attendance: PoolObjectBase<'attendance'> & {
    eventId: string
    userId: string | null
    guestId: string | null
    // The member who brings (and is billed for) a guest; null on member rows.
    hostUserId: string | null
    status: AttendanceStatus
    // Flat ISO day set; null means "whole event". Only meaningful when going.
    days: string[] | null
    createdByUserId: string | null
    createdAt: string
  }
  // Workspace-level identity for a non-member brought along by a member.
  guest: PoolObjectBase<'guest'> & {
    workspaceId: string
    name: string
    // Synthesized by the plus-ones backfill; hidden from pickers until renamed.
    placeholder: boolean
    createdByUserId: string | null
    createdAt: string
  }
  workspace: PoolObjectBase<'workspace'> & {
    name: string
    // The group's default IANA zone; events fall back to it.
    timezone: string
    memberIds: string[]
    createdAt: string
  }
  taskList: PoolObjectBase<'taskList'> & {
    workspaceId: string
    userId: string | null
    name: string
    position: number
    createdAt: string
  }
  taskItem: PoolObjectBase<'taskItem'> & {
    taskListId: string
    userId: string | null
    content: string
    completedAt: string | null
    position: number
    createdAt: string
  }
  expense: PoolObjectBase<'expense'> & {
    eventId: string
    userId: string | null
    createdByUserId: string | null
    settlementId: string | null
    revertsExpenseId: string | null
    description: string
    amount: number
    startDate: string
    endDate: string
    participantIds: string[]
    createdAt: string
  }
  settlement: PoolObjectBase<'settlement'> & {
    eventId: string
    userId: string | null
    previousSettlementId: string | null
    transferIds: string[]
    createdAt: string
  }
  settlementTransfer: PoolObjectBase<'settlementTransfer'> & {
    settlementId: string
    fromUserId: string | null
    toUserId: string | null
    amount: number
    paidAt: string | null
    paidByUserId: string | null
    supersededAt: string | null
    createdAt: string
  }
  choreRoster: PoolObjectBase<'choreRoster'> & {
    eventId: string
    userId: string | null
    choreIds: string[]
    createdAt: string
  }
  chore: PoolObjectBase<'chore'> & {
    choreRosterId: string
    name: string
    peoplePerDay: number
    position: number
    // Optional wall-clock "HH:MM" time the chore is due; drives reminders.
    time: string | null
    assignmentIds: string[]
    createdAt: string
  }
  choreAssignment: PoolObjectBase<'choreAssignment'> & {
    choreId: string
    // The attendance row behind the holder (doc/attendances.md phase 8).
    // Null only on legacy rows written before the link existed.
    attendanceId: string | null
    // The member behind the holder, mirrored from the attendance for old
    // clients; null once guest attendances can hold chores.
    userId: string | null
    date: string
    pinned: boolean
    note: string | null
    createdAt: string
  }
  expenseParticipant: PoolObjectBase<'expenseParticipant'> & {
    expenseId: string
    userId: string
    factor: number
    createdAt: string
  }
  workspaceInvite: PoolObjectBase<'workspaceInvite'> & {
    workspaceId: string
    invitedBy: string | null
    email: string
    name: string | null
    expiresAt: string
    acceptedAt: string | null
    lastRemindedAt: string | null
    createdAt: string
  }
  notification: PoolObjectBase<'notification'> & {
    userId: string
    workspaceId: string | null
    kind: string
    data: {
      title?: string
      body?: string
      href?: string
      [key: string]: unknown
    }
    readAt: string | null
    createdAt: string
  }
}

// ============================================================================
// DERIVED TYPES - Don't modify these
// ============================================================================

export type ObjectType = (typeof OBJECT_TYPES)[number]
export type PoolObject = ObjectTypeMap[ObjectType]

// Convenience aliases for accessing specific pool types
export type PoolMember = ObjectTypeMap['member']
export type PoolEvent = ObjectTypeMap['event']
export type PoolDatePoll = ObjectTypeMap['datePoll']
export type PoolDateRange = ObjectTypeMap['dateRange']
export type PoolVote = ObjectTypeMap['vote']
export type PoolAttendance = ObjectTypeMap['attendance']
export type PoolGuest = ObjectTypeMap['guest']
export type PoolWorkspace = ObjectTypeMap['workspace']
export type PoolTaskList = ObjectTypeMap['taskList']
export type PoolTaskItem = ObjectTypeMap['taskItem']
export type PoolExpense = ObjectTypeMap['expense']
export type PoolSettlement = ObjectTypeMap['settlement']
export type PoolSettlementTransfer = ObjectTypeMap['settlementTransfer']
export type PoolChoreRoster = ObjectTypeMap['choreRoster']
export type PoolChore = ObjectTypeMap['chore']
export type PoolChoreAssignment = ObjectTypeMap['choreAssignment']
export type PoolExpenseParticipant = ObjectTypeMap['expenseParticipant']
export type PoolWorkspaceInvite = ObjectTypeMap['workspaceInvite']
export type PoolNotification = ObjectTypeMap['notification']

// API response wrapper - all endpoints include objects array
export interface PoolApiResponse {
  objects: PoolObject[]
}

// Pending update for optimistic updates
export interface PendingUpdate<T extends ObjectType = ObjectType> {
  id: string
  objectType: T
  objectId: string
  changes: Partial<ObjectTypeMap[T]>
  timestamp: number
}
